/**
 * ThemeReplaceModal — month-level "Change theme" module for the Annual
 * Plan. Two paths: pick a canonical library theme (with 4-topic preview)
 * or build a bespoke theme + 4 weekly topics with AI from a brief.
 * Extracted from AnnualPlanView.jsx during the code-quality refactor.
 */
import { useEffect, useState } from "react";
import axios from "axios";

import { API, profileFields } from "../lib/beaconUtils";

export function ThemeReplaceModal({ month, themeInfo, appliedProfile,
                                    planId = "default", onClose, onApplied }) {
  const [tab, setTab] = useState("library");
  const [themes, setThemes] = useState([]);
  const [pickedThemeId, setPickedThemeId] = useState(null);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/plan/themes`)
      .then((r) => setThemes(r.data.themes))
      .catch((e) => setError(`Could not load the theme library: ${e.message}`));
  }, []);

  const apply = async (body) => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/plan/replace-theme`, {
        month, plan_id: planId, ...profileFields(appliedProfile), ...body,
      });
      await onApplied();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="beacon-modal-backdrop" data-testid="theme-modal-backdrop"
      onClick={() => !busy && onClose()}>
      <div className="beacon-modal beacon-theme-modal" data-testid="theme-replace-modal"
        onClick={(e) => e.stopPropagation()}>
        <div className="beacon-modal-head">
          <div>
            <div className="beacon-tiny-label">CHANGE THEME · {month.toUpperCase()}</div>
            <div className="beacon-theme-modal-current">
              Currently: <strong>{themeInfo.theme_name}</strong>
            </div>
          </div>
          <button type="button" className="beacon-rationale-panel-close"
            data-testid="theme-modal-close" onClick={onClose}
            aria-label="Close" disabled={busy}>×</button>
        </div>

        <div className="beacon-theme-modal-tabs">
          <button type="button"
            className={`beacon-theme-modal-tab${tab === "library" ? " is-active" : ""}`}
            data-testid="theme-modal-tab-library"
            onClick={() => setTab("library")}>From library</button>
          <button type="button"
            className={`beacon-theme-modal-tab${tab === "ai" ? " is-active" : ""}`}
            data-testid="theme-modal-tab-ai"
            onClick={() => setTab("ai")}>Build with AI</button>
        </div>

        {error && (
          <div className="beacon-tab-error" data-testid="theme-modal-error">{error}</div>
        )}

        {tab === "library" && (
          <>
            <div className="beacon-theme-modal-list" data-testid="theme-modal-list">
              {themes.map((th) => (
                <button key={th.theme_id} type="button"
                  className={`beacon-theme-option${
                    pickedThemeId === th.theme_id ? " is-picked" : ""
                  }`}
                  data-testid={`theme-option-${th.theme_id}`}
                  onClick={() => setPickedThemeId(th.theme_id)}>
                  <span className="beacon-theme-option-head">
                    <strong>{th.theme_name}</strong>
                    {th.mental_health_related && (
                      <span className="beacon-theme-mh-chip">mental health</span>
                    )}
                  </span>
                  <span className="beacon-theme-option-topics">
                    {th.topics.map((t) => t.topic_name).join(" · ")}
                  </span>
                </button>
              ))}
            </div>
            <div className="beacon-theme-modal-actions">
              {themeInfo.overridden && (
                <button type="button" className="beacon-secondary-btn"
                  data-testid="theme-modal-revert"
                  disabled={busy}
                  onClick={() => apply({ mode: "revert" })}>
                  Restore base theme
                </button>
              )}
              <button type="button" className="beacon-primary-btn"
                data-testid="theme-modal-apply"
                disabled={!pickedThemeId || busy}
                onClick={() => apply({ mode: "library", theme_id: pickedThemeId })}>
                {busy ? (
                  <><span className="beacon-spinner beacon-spinner-inline" />Applying…</>
                ) : (
                  <>Apply theme to {month} →</>
                )}
              </button>
            </div>
          </>
        )}

        {tab === "ai" && (
          <>
            <p className="beacon-theme-modal-hint">
              Describe the month you want — Claude builds a bespoke theme
              with 4 weekly topics tailored to your Context Engine profile.
              For example: &ldquo;working at heights refresher month for
              scaffold crews&rdquo;.
            </p>
            <textarea
              className="beacon-rationale-panel-textarea"
              data-testid="theme-ai-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Your brief for the month (max 500 chars)"
              maxLength={500}
              rows={4}
              disabled={busy}
            />
            <div className="beacon-theme-modal-actions">
              <button type="button" className="beacon-primary-btn"
                data-testid="theme-ai-build"
                disabled={brief.trim().length < 4 || busy}
                onClick={() => apply({ mode: "custom", brief: brief.trim() })}>
                {busy ? (
                  <><span className="beacon-spinner beacon-spinner-inline" />
                    Claude is building the month… (20–40s)</>
                ) : (
                  <>Build &amp; apply to {month} →</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
